import React, { useCallback } from "react";
import { Box, Button, Typography, Stack } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import RelationshipFilter, {
  RelationshipFilterData,
} from "./RelationshipFilter";
import { CompanyOption } from "./VirtualizedCompanyDropdown";

interface RelationshipFiltersProps {
  filters: RelationshipFilterData[];
  onChange: (filters: RelationshipFilterData[]) => void;
}

const RelationshipFilters: React.FC<RelationshipFiltersProps> = ({
  filters,
  onChange,
}) => {
  // Generate unique ID for new filters
  const generateFilterId = useCallback(() => {
    return `rel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add new relationship filter
  const handleAddFilter = useCallback(() => {
    const newFilter: RelationshipFilterData = {
      id: generateFilterId(),
      relationshipTypes: [],
      targetCompanies: [],
      maxTiers: 1,
    };
    onChange([...filters, newFilter]);
  }, [filters, onChange, generateFilterId]);

  // Update specific filter
  const handleUpdateFilter = useCallback(
    (filterId: string, updatedFilter: RelationshipFilterData) => {
      const updatedFilters = filters.map((filter) =>
        filter.id === filterId ? updatedFilter : filter,
      );
      onChange(updatedFilters);
    },
    [filters, onChange],
  );

  // Remove specific filter
  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      const updatedFilters = filters.filter((filter) => filter.id !== filterId);
      onChange(updatedFilters);
    },
    [filters, onChange],
  );

  return (
    <Box>
      {/* Add filter button */}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddFilter}
        size="small"
        fullWidth
        sx={{
          mb: 2,
          borderStyle: "dashed",
          color: "text.secondary",
          borderColor: "divider",
          "&:hover": {
            borderColor: "primary.main",
            color: "primary.main",
            backgroundColor: "primary.light",
          },
        }}
      >
        Add Relationship Filter
      </Button>

      {/* Empty state */}
      {filters.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 3,
            color: "text.secondary",
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            No relationship filters added yet
          </Typography>
          <Typography variant="caption">
            Find organizations with specific relationships to other companies in
            the database
          </Typography>
        </Box>
      )}

      {/* Filter list */}
      {filters.length > 0 && (
        <Stack spacing={0}>
          {filters.map((filter, index) => (
            <Box key={filter.id}>
              <RelationshipFilter
                filter={filter}
                onUpdate={(updatedFilter) =>
                  handleUpdateFilter(filter.id, updatedFilter)
                }
                onRemove={() => handleRemoveFilter(filter.id)}
              />

              {/* AND logic indicator between filters */}
              {index < filters.length - 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    my: 1,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    AND
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* Logic explanation */}
      {filters.length > 1 && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            backgroundColor: "transparent",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: "medium" }}
          >
            Logic: All relationship filters must match (AND). Within each
            filter, relationship types and companies are combined with OR.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RelationshipFilters;
